import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Link,
  Chip,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { BackButton } from '../../common/BackButton';
import { useCreateShipment } from './useCreateShipment';
import { ShipmentForm } from './components';
import { shipmentStatusLabel, shipmentStatusColor } from '../../../utils/shipmentStatus';
import type { ShipmentFormValues } from './useCreateShipment';

const EMPTY_FORM: ShipmentFormValues = {
  recipientLastName: '',
  recipientFirstName: '',
  recipientMiddleName: '',
  recipientPhone: '',
  recipientCity: null,
  recipientWarehouse: null,
  senderId: '',
  senderContactPerson: null,
  weight: '',
  seatsAmount: '1',
  declaredValue: '',
  codEnabled: false,
  codAmount: '',
  payerType: 'SENDER',
  paymentMethod: 'CASH',
};

export default function CreateShipmentPage() {
  const { id, groupId } = useParams<{ id: string; groupId: string }>();
  const navigate = useNavigate();

  if (!id || !groupId) {
    throw new Error('Company ID and Group ID are required');
  }

  const companyId = parseInt(id);
  const groupIdNum = parseInt(groupId);
  const activitiesPath = `/companies/${companyId}/activities`;

  const {
    data: {
      loading,
      error,
      eligibilityError,
      client,
      senders,
      existingShipment,
      apiKey,
      selectSenderError,
    },
    actions: { selectSender },
    submit,
    refreshStatus,
    cancel,
  } = useCreateShipment({ companyId, groupId: groupIdNum });

  const [formValues, setFormValues] = useState<ShipmentFormValues>(EMPTY_FORM);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [forceCancelDialogOpen, setForceCancelDialogOpen] = useState(false);

  const handleConfirmCancel = async () => {
    await cancel.run();
    setCancelDialogOpen(false);
  };

  const handleConfirmForceCancel = async () => {
    await cancel.forceRun();
    setForceCancelDialogOpen(false);
  };

  // declaredValue/codAmount are never prefilled from the activity group's sale total —
  // that total is in USD (CRM's only currency, see doc/pricingV2/1-prices.md), while Nova
  // Poshta needs a hryvnia amount, so the user must enter both by hand.
  useEffect(() => {
    if (!client) return;
    setFormValues((prev) => ({
      ...prev,
      recipientLastName: client.lastName,
      recipientFirstName: client.firstName,
      recipientMiddleName: client.middleName ?? '',
      recipientPhone: client.phoneNumber ?? '',
    }));
  }, [client]);

  // Defaults the form to the company's first sender profile so the page is usable without
  // an extra click for the common single-profile case; the user can still switch it.
  useEffect(() => {
    const firstSender = senders[0];
    if (!firstSender || formValues.senderId) return;
    setFormValues((prev) => ({ ...prev, senderId: firstSender.id }));
    selectSender(firstSender.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senders]);

  const handleFormChange = (next: ShipmentFormValues) => {
    if (next.senderId && next.senderId !== formValues.senderId) {
      selectSender(next.senderId);
      // The previously selected contact person belongs to the old sender's own Nova
      // Poshta account — not valid once the API key behind the autocomplete changes.
      setFormValues({ ...next, senderContactPerson: null });
      return;
    }
    setFormValues(next);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const senderConfigured = senders.length > 0;
  const selectedSender = senders.find((s) => s.id === formValues.senderId) ?? null;
  const selectedSenderMissingAddress =
    Boolean(selectedSender) && (!selectedSender?.defaultSenderCityRef || !selectedSender?.defaultSenderWarehouseRef);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <BackButton
        label="Назад"
        onClick={() => navigate(activitiesPath)}
      />

      <Typography variant="h4" sx={{ mb: 2 }}>Створити ТТН</Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {eligibilityError && <Alert severity="warning">{eligibilityError}</Alert>}

      {!error && !eligibilityError && !existingShipment && !senderConfigured && (
        <Alert severity="warning">
          Спершу налаштуйте API-ключ і склад відправлення в{' '}
          <Link component={RouterLink} to={`/companies/${companyId}/settings`}>
            Налаштуваннях компанії
          </Link>.
        </Alert>
      )}

      {!error && !eligibilityError && !existingShipment && senderConfigured && selectedSenderMissingAddress && (
        <Alert severity="warning">
          У профілю «{selectedSender?.label}» не вказано місто й відділення відправлення — додайте їх у{' '}
          <Link component={RouterLink} to={`/companies/${companyId}/settings`}>
            Налаштуваннях компанії
          </Link>, або оберіть інший профіль нижче.
        </Alert>
      )}

      {!error && !eligibilityError && !existingShipment && selectSenderError && (
        <Alert severity="error">{selectSenderError}</Alert>
      )}

      {!error && !eligibilityError && existingShipment && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            ТТН вже створено
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Номер: <strong>{existingShipment.ttnNumber}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Відправник: {existingShipment.senderLabel}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={shipmentStatusLabel(existingShipment.status)}
              color={shipmentStatusColor(existingShipment.status)}
              size="small"
            />
            <Button
              size="small"
              color="inherit"
              startIcon={refreshStatus.submitting ? <CircularProgress size={14} /> : <RefreshIcon />}
              disabled={refreshStatus.submitting}
              onClick={refreshStatus.run}
            >
              Оновити статус
            </Button>
            <Tooltip title={existingShipment.ttnRef ? '' : 'Створено до підтримки скасування — скасуйте вручну в кабінеті Нової пошти'}>
              <span>
                <Button
                  size="small"
                  color="error"
                  disabled={!existingShipment.ttnRef || cancel.submitting}
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Скасувати ТТН
                </Button>
              </span>
            </Tooltip>
          </Box>
          {refreshStatus.error && (
            <Alert severity="error" sx={{ mt: 1 }}>{refreshStatus.error}</Alert>
          )}
          {cancel.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {cancel.error}
              <Box sx={{ mt: 1 }}>
                <Button size="small" color="error" onClick={() => setForceCancelDialogOpen(true)}>
                  Позначити скасованою вручну
                </Button>
              </Box>
            </Alert>
          )}
        </Paper>
      )}

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Скасувати ТТН?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Експрес-накладну {existingShipment?.ttnNumber} буде скасовано в Новій пошті. Після цього можна буде створити нову відправку для цієї групи активностей.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancel.submitting}>
            Не скасовувати
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            disabled={cancel.submitting}
          >
            {cancel.submitting ? 'Скасування...' : 'Скасувати ТТН'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forceCancelDialogOpen} onClose={() => setForceCancelDialogOpen(false)}>
        <DialogTitle>Позначити скасованою вручну?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Нова пошта відхиляє скасування експрес-накладної {existingShipment?.ttnNumber} через наш API. Використовуйте цю дію, лише якщо ви вже бачите цю накладну скасованою/видаленою в кабінеті Нової пошти.
            CRM позначить відправку скасованою без підтвердження від Нової пошти, а група активностей звільниться для нової відправки.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceCancelDialogOpen(false)} disabled={cancel.submitting}>
            Скасувати
          </Button>
          <Button
            onClick={handleConfirmForceCancel}
            color="error"
            disabled={cancel.submitting}
          >
            {cancel.submitting ? 'Виконання...' : 'Позначити скасованою'}
          </Button>
        </DialogActions>
      </Dialog>

      {!error && !eligibilityError && senderConfigured && !existingShipment && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <ShipmentForm
            values={formValues}
            onChange={handleFormChange}
            senders={senders}
            apiKey={apiKey}
            submitting={submit.submitting}
            error={submit.error}
            onSubmit={async () => {
              const result = await submit.run(formValues);
              if (!result) {
                navigate(activitiesPath);
              }
            }}
          />
        </Paper>
      )}
    </Box>
  );
}
